import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import useRecordings from "../../hooks/useRecordings";
import ConfirmDialog from "../../component/ui/ConfirmDialog";
import formatDateTime from "../../utils/formatDateTime";
import { downloadFile } from "../../utils/download";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/interview/_recordings-tab.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDuration(ms) {
    if (!ms && ms !== 0) return null;
    const sec = Math.floor(ms / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    if (m === 0) return `${s}s`;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function prettyQuestion(slugValue) {
    if (!slugValue) return "Untitled question";
    return slugValue.replaceAll("-", " ");
}

function recCodeFromFilename(filename) {
    if (!filename) return null;
    const match = String(filename).match(/-(\d+)\.(\w+)$/);
    if (!match) return null;
    return `REC-${match[1].slice(-6)}`;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconPlay = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="11"
            cy="11"
            r="10"
            fill="rgba(255,255,255,0.15)"
            stroke="rgba(255,255,255,0.55)"
            strokeWidth="1.2"
        />
        <path d="M9 7.5l7 3.5-7 3.5V7.5z" fill="#fff" />
    </svg>
);

const IconTrash = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M1.5 3.5h11M4.5 3.5V2.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v1M5.5 6.5v4M8.5 6.5v4M2.5 3.5l.9 8a.5.5 0 0 0 .5.5h6.2a.5.5 0 0 0 .5-.5l.9-8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconDownload = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7 2v7M4 6.5L7 9.5l3-3M2 12h10"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconRecord = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="8" cy="8" r="5" fill="currentColor" opacity="0.2" />
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="8" cy="8" r="2.5" fill="currentColor" />
    </svg>
);

const IconFilm = () => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="5"
            y="10"
            width="30"
            height="20"
            rx="2.5"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path
            d="M5 15h5M5 20h5M5 25h5M30 15h5M30 20h5M30 25h5M15 10v20M25 10v20"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

const IconWarning = () => (
    <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M14 3L25.5 24H2.5L14 3z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path
            d="M14 11v6M14 19.5v1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

// ─── Recording card ───────────────────────────────────────────────────────────
function RecordingCard({ rec, onDelete, onDownload, onOpen, index }) {
    const [hovered, setHovered] = useState(false);

    const title = prettyQuestion(rec.question_slug);
    const duration = fmtDuration(rec.duration_ms);
    const date = formatDateTime(rec.created_at);
    const recCode = recCodeFromFilename(rec.filename);
    const isTranscriptPending = rec.transcript_status === "pending";
    const rawScore = Number(rec.feedback_score);
    const isProAiPlan =
        String(rec?.plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";
    const isFeedbackPending = isProAiPlan && rec.feedback_status === "pending";
    const isPending = isTranscriptPending || isFeedbackPending;
    const pendingLabel = isFeedbackPending
        ? "Generating feedback…"
        : isTranscriptPending
          ? "Generating transcript…"
          : "Processing…";
    const hasFeedback = isProAiPlan && Number.isFinite(rawScore);
    const scoreValue = hasFeedback ? rawScore : null;
    const scoreBucket =
        scoreValue === null
            ? null
            : scoreValue >= 7
              ? "high"
              : scoreValue >= 4
                ? "mid"
                : "low";

    return (
        <article
            className={`rec-card${isPending ? " rec-card--pending" : ""}${hasFeedback ? " rec-card--scored" : ""}`}
            role="listitem"
            style={{ "--i": index }}
        >
            {/* Thumbnail */}
            <button
                type="button"
                className="rec-card__thumb-btn"
                onClick={() => {
                    if (!isPending) onOpen(rec);
                }}
                aria-label={`Open recording: ${title}`}
                aria-disabled={isPending}
                disabled={isPending}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className="rec-card__thumb">
                    <video
                        src={rec.url}
                        preload="metadata"
                        muted
                        playsInline
                        className="rec-card__video"
                        onMouseEnter={(e) => {
                            if (!isPending)
                                e.currentTarget.play?.().catch(() => {});
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.pause?.();
                            e.currentTarget.currentTime = 0;
                        }}
                    />

                    {/* Gradient overlay */}
                    <div
                        className="rec-card__thumb-gradient"
                        aria-hidden="true"
                    />

                    {/* Play indicator */}
                    {!isPending && (
                        <div
                            className={`rec-card__play${hovered ? " is-visible" : ""}`}
                            aria-hidden="true"
                        >
                            <IconPlay />
                        </div>
                    )}

                    {/* Duration badge */}
                    {duration && !isPending && (
                        <span className="rec-card__duration" aria-hidden="true">
                            {duration}
                        </span>
                    )}

                    {/* Pending overlay */}
                    {isPending && (
                        <div
                            className="rec-card__pending-overlay"
                            aria-hidden="true"
                        >
                            <span className="rec-card__spinner" />
                            <span className="rec-card__pending-label">
                                {pendingLabel}
                            </span>
                        </div>
                    )}

                    {/* Score badge */}
                    {scoreValue !== null && !isPending && (
                        <div
                            className={`rec-card__score-badge rec-card__score-badge--${scoreBucket}`}
                            aria-label={`Score: ${scoreValue}/10`}
                        >
                            {scoreValue}
                            <span>/10</span>
                        </div>
                    )}
                </div>
            </button>

            {/* Body */}
            <div className="rec-card__body">
                <div className="rec-card__body-top">
                    <p className="rec-card__title">{title}</p>
                    {recCode && (
                        <span className="rec-card__code">{recCode}</span>
                    )}
                </div>

                <div className="rec-card__footer">
                    {date && <span className="rec-card__date">{date}</span>}

                    <div className="rec-card__actions">
                        <button
                            type="button"
                            className="rc-btn rc-btn--danger"
                            aria-label="Delete recording"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(rec);
                            }}
                        >
                            <IconTrash />
                        </button>
                        <button
                            type="button"
                            className="rc-btn"
                            aria-label="Download recording"
                            onClick={(e) => {
                                e.stopPropagation();
                                void onDownload(rec);
                            }}
                        >
                            <IconDownload />
                        </button>
                    </div>
                </div>
            </div>
        </article>
    );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function SkeletonCard({ index }) {
    return (
        <div className="rec-skel" aria-hidden="true" style={{ "--i": index }}>
            <div className="rec-skel__thumb sk" />
            <div className="rec-skel__body">
                <div className="sk sk--title" />
                <div className="sk sk--meta" />
            </div>
        </div>
    );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export default function RecordingsTab() {
    const navigate = useNavigate();
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();

    const [token, setToken] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [plan, setPlan] = useState("free");

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isAuthenticated) {
                if (!cancelled) setToken(null);
                return;
            }
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);
            } catch {
                if (!cancelled) setToken(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, getAccessTokenSilently]);

    React.useEffect(() => {
        if (!token) return;
        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/profile`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: ac.signal,
                    }
                );
                if (!res.ok) return;
                const data = await res.json();
                setPlan(data?.plan || "free");
            } catch {}
        })();
        return () => ac.abort();
    }, [token]);

    const {
        items: recordings = [],
        loading,
        error,
        remove: deleteRecording,
    } = useRecordings({ token });

    const sorted = useMemo(
        () =>
            [...recordings].sort(
                (a, b) =>
                    new Date(b.created_at || 0) - new Date(a.created_at || 0)
            ),
        [recordings]
    );

    const onOpen = (rec) => {
        const id = rec.id ?? rec.recording_id;
        if (!rec.question_slug || !id) return;
        navigate(`/dashboard/questions/${rec.question_slug}/recording/${id}`);
    };

    const onDownload = async (rec) => {
        if (!rec) return;
        const id = rec.id ?? rec.recording_id;
        const baseName = (
            rec.filename ||
            rec.question_slug ||
            "recording"
        ).replace(/\.\w+$/, "");
        if (!id) {
            const fallbackExt = rec.url?.split(".").pop() || "webm";
            const filename = `${baseName}.${fallbackExt}`;
            await downloadFile({ url: rec.url, filename, token });
            return;
        }
        const url = `${API_BASE}/me/recordings/${id}/download?format=mp4`;
        const filename = `${baseName}.mp4`;
        await downloadFile({ url, filename, token });
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        await deleteRecording(pendingDelete.id);
        setPendingDelete(null);
    };

    const count = sorted.length;

    return (
        <div className="rt">
            {/* ── Header ── */}
            <div className="rt__header">
                <div className="rt__header-text">
                    <p className="rt__eyebrow">Library</p>
                    <h1 className="rt__title">Recordings</h1>
                    <p className="rt__sub">
                        Review your practice sessions and feedback.
                    </p>
                </div>
                <div className="rt__header-actions">
                    {!loading && count > 0 && (
                        <span className="rt__count">
                            {count} {count === 1 ? "recording" : "recordings"}
                        </span>
                    )}
                    <Link to="/dashboard/questions" className="rt__record-btn">
                        New recording
                    </Link>
                </div>
            </div>

            {/* ── Content ── */}
            {loading ? (
                <div
                    className="rec-grid"
                    aria-label="Loading recordings"
                    aria-busy="true"
                >
                    {Array.from({ length: 6 }).map((_, i) => (
                        <SkeletonCard key={i} index={i} />
                    ))}
                </div>
            ) : error ? (
                <div className="rt__state rt__state--error" role="alert">
                    <div className="rt__state-icon">
                        <IconWarning />
                    </div>
                    <div className="rt__state-body">
                        <p className="rt__state-title">
                            Could not load recordings
                        </p>
                        <p className="rt__state-text">
                            Refresh the page. If the problem persists, try again
                            later.
                        </p>
                    </div>
                </div>
            ) : count === 0 ? (
                <div className="rt__state" role="status">
                    <div className="rt__state-icon">
                        <IconFilm />
                    </div>
                    <div className="rt__state-body">
                        <p className="rt__state-title">No recordings yet</p>
                        <p className="rt__state-text">
                            Record your first practice answer and it will appear
                            here.
                        </p>
                        <Link
                            to="/dashboard/questions"
                            className="rt__record-btn rt__record-btn--inline"
                        >
                            Go to question bank
                        </Link>
                    </div>
                </div>
            ) : (
                <div
                    className="rec-grid"
                    role="list"
                    aria-label="Your recordings"
                >
                    {sorted.map((rec, i) => (
                        <RecordingCard
                            key={rec.id ?? rec.recording_id}
                            rec={{ ...rec, plan }}
                            index={i}
                            onOpen={onOpen}
                            onDelete={setPendingDelete}
                            onDownload={onDownload}
                        />
                    ))}
                </div>
            )}

            <ConfirmDialog
                open={!!pendingDelete}
                title="Delete this recording?"
                description="This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                tone="danger"
                onCancel={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
